import { initializeApp } from 'firebase/app';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getDatabase, connectDatabaseEmulator } from 'firebase/database';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getAnalytics, isSupported } from 'firebase/analytics';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "demo-api-key",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "demo-quien-es-mas-probable.firebaseapp.com",
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL || "https://demo-quien-es-mas-probable-default-rtdb.firebaseio.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "demo-quien-es-mas-probable",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "demo-quien-es-mas-probable.appspot.com",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "123456789",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:123456789:web:abcdef123456",
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
console.log('🔥 Initializing Firebase with config:', firebaseConfig);
const app = initializeApp(firebaseConfig);

// Initialize services with error handling
let db: any, rtdb: any, auth: any, analytics: any;

try {
  db = getFirestore(app);
  console.log('🔥 Firestore initialized successfully');
} catch (error) {
  console.error('❌ Error initializing Firestore:', error);
  throw error;
}

try {
  rtdb = getDatabase(app);
  console.log('🔥 Database initialized successfully');
} catch (error) {
  console.error('❌ Error initializing Database:', error);
  throw error;
}

try {
  auth = getAuth(app);
  console.log('🔥 Auth initialized successfully');
} catch (error) {
  console.error('❌ Error initializing Auth:', error);
  throw error;
}

// Initialize Analytics (only in production and supported browsers)
const isDev = import.meta.env.DEV;
if (!isDev && typeof window !== 'undefined') {
  isSupported().then((supported) => {
    if (supported) {
      try {
        analytics = getAnalytics(app);
        console.log('📊 Analytics initialized successfully');
      } catch (error) {
        console.warn('⚠️ Analytics initialization failed:', error);
      }
    } else {
      console.warn('⚠️ Analytics not supported in this browser');
    }
  }).catch((error) => {
    console.warn('⚠️ Error checking Analytics support:', error);
  });
}

export { db, rtdb, auth, app, analytics };

// Connect to emulators if in development
const useEmulators = import.meta.env.VITE_USE_EMULATORS === 'true';

let emulatorsConnected = false;

async function connectToEmulators() {
  if (emulatorsConnected || !useEmulators || typeof window === 'undefined') {
    return;
  }

  console.log('🔧 Connecting to Firebase emulators...');
  
  // Connect to Auth emulator first (most critical)
  try {
    connectAuthEmulator(auth, 'http://localhost:9098', { disableWarnings: true });
    console.log('🔥 Connected to Auth Emulator on :9098');
  } catch (error) {
    console.log('⚠️ Auth emulator connection failed or already connected');
  }
  
  // Connect to Firestore emulator
  try {
    connectFirestoreEmulator(db, 'localhost', 8081);
    console.log('🔥 Connected to Firestore Emulator on :8081');
  } catch (error) {
    console.log('⚠️ Firestore emulator connection failed or already connected');
  }
  
  // Connect to Database emulator
  try {
    connectDatabaseEmulator(rtdb, 'localhost', 9001);
    console.log('🔥 Connected to Database Emulator on :9001');
  } catch (error) {
    console.log('⚠️ Database emulator connection failed or already connected');
  }
  
  emulatorsConnected = true;
}

// Initialize emulator connections
if (useEmulators && typeof window !== 'undefined') {
  // Try connecting immediately
  connectToEmulators();
}

// Log configuration for debugging
console.log('🔥 Firebase initialized:', {
  projectId: firebaseConfig.projectId,
  useEmulators,
  env: import.meta.env.MODE
});

export default app;